/**
 * 業務ワークフローの初期雛形 (backend `DefaultWorkflowTemplates`) が持つ日本語の値を、
 * 表示言語に合わせて出す純関数群 (#11795)。
 *
 * 初期雛形の雛形名・説明・タグ・ステップ名は組織のデータとして日本語のまま保存され、
 * 雛形から作った業務ワークフローにも日本語でコピーされる。 表示する値が初期値の日本語と
 * 一致する時だけ訳を出し、お客様が書き換えた値は書かれたまま出す (spec
 * `workflow/workflow-templates.md`「英語表示での初期雛形の値」)。 初期値の日本語の正本は
 * ja 辞書の `page:workflowBuiltin` 節で、
 * backend の定義とのずれは Go のテスト (`default_workflow_templates_i18n_test.go`) が止める。
 *
 * 保存する値は常に日本語。 編集フォームの入力値には通さない (英語で上書き保存されると、
 * 日本語表示のメンバーにも英語が出るため)。
 */
import i18next, { type TFunction } from 'i18next'

const SECTION = 'workflowBuiltin'

type BuiltinGroups = Record<string, Record<string, string>>

function builtinGroups(): BuiltinGroups {
  const page = i18next.getResourceBundle('ja', 'page') as Record<
    string,
    unknown
  >
  return page[SECTION] as BuiltinGroups
}

/** 初期値の日本語 → i18n key。 i18n の初期化後に初めて使う時に作る。 */
let i18nKeyByJapanese: Map<string, string> | undefined

function keyByJapanese(): Map<string, string> {
  if (i18nKeyByJapanese !== undefined) return i18nKeyByJapanese
  const byJapanese = new Map<string, string>()
  for (const [group, entries] of Object.entries(builtinGroups())) {
    for (const [key, japanese] of Object.entries(entries)) {
      // 同じ日本語が 2 つの key に入ると、どちらの訳を出すかが決まらない
      if (byJapanese.has(japanese)) {
        throw new Error(`workflowBuiltin の日本語が重複している: ${japanese}`)
      }
      byJapanese.set(japanese, `page:${SECTION}.${group}.${key}`)
    }
  }
  i18nKeyByJapanese = byJapanese
  return byJapanese
}

/**
 * 初期雛形の値なら表示言語の訳を、それ以外 (お客様が書いた値) はそのまま返す。
 * 日本語表示では初期値も同じ日本語が返る。
 */
export function localizeBuiltinWorkflowText(
  value: string,
  t: TFunction,
): string {
  const key = keyByJapanese().get(value)
  return key === undefined ? value : t(key)
}
